import React, { useEffect, useState } from 'react';

import AttachMoneyIcon from '@mui/icons-material/AttachMoneyOutlined';
import Diversity2Icon from '@mui/icons-material/Diversity2Outlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Navigate, useParams,
} from 'react-router-dom';

import PageContent from '../../../components/PageContent';
import { LoadingWrapper } from '../../../components/PageLayout';
import Tabs from '../../../components/Tabs';
import { LessonEnrolment } from '../../../services/classes/types';
import { useGetStudentQuery } from '../../../services/students';
import useTab from '../../../utils/react/hooks/useTab';
import { ContentContainer, PageContainer } from '../index.styles';

import BillingTab from './BillingTab';
import ClassesTab from './ClassesTab';
import StudentLessonDetails from './ClassesTab/StudentLessonDetails';
import ProfileTab from './ProfileTab';
import SettingsTab from './SettingsTab';
import TopBar from './TopBar';

const TAB_OPTIONS = [
  {
    key: 'profile',
    label: 'Profile',
    icon: <PersonOutlinedIcon />,
  },
  {
    key: 'classes',
    label: 'Classes',
    icon: <Diversity2Icon />,
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: <AttachMoneyIcon />,
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <SettingsOutlinedIcon />,
  },
];

const AdminStudentPage = () => {
  const { studentId } = useParams();
  // If no userId is provided, return to Students page
  if (!studentId) return <Navigate replace to="/admin/students" />;

  const [tab, setTab] = useTab({ default: TAB_OPTIONS[0].key });
  const [selectedLesson, setSelectedLesson] = useState<LessonEnrolment | undefined>();

  const { data: student,
    isLoading: isStudentDataLoading } = useGetStudentQuery(
    { studentId },
  );

  // Update page title
  useEffect(() => {
    if (student?.data.name) {
      document.title = `${student?.data.name} | Cognito Students`;
    }
  }, [student]);

  useEffect(() => {
    if (tab !== 'lessons') {
      setSelectedLesson(undefined);
    }
  }, [tab]);

  const deselectLesson = () => {
    setSelectedLesson(undefined);
  };

  const handleSelect = (lesson: LessonEnrolment) => {
    if (selectedLesson?.id === lesson.id) {
      deselectLesson();
    } else {
      setSelectedLesson(lesson);
    }
  };

  return (
    <PageContent
      sideBar={selectedLesson ? (
        <StudentLessonDetails
          selectedLesson={selectedLesson}
          onClose={() => deselectLesson()}
        />
      ) : <div />}
      sideBarOpen={!!selectedLesson}
      sideBarWidth={600}
    >
      <PageContainer>
        <LoadingWrapper loading={isStudentDataLoading}>
          <ContentContainer>
            <TopBar student={student?.data} />
            <Tabs options={TAB_OPTIONS} selectedKey={tab} onChange={(key) => setTab(key)} />
            {
              tab === 'profile' && <ProfileTab student={student?.data} studentMetadata={student?.metadata} />
            }
            {
              tab === 'classes' && (
                <ClassesTab
                  student={student?.data}
                  selectedLesson={selectedLesson}
                  onSelectLesson={handleSelect}
                />
              )
            }
            {
              tab === 'billing' && <BillingTab student={student?.data} studentMetadata={student?.metadata} />
            }
            {
              tab === 'settings' && <SettingsTab student={student?.data} studentMetadata={student?.metadata} />
            }
          </ContentContainer>
        </LoadingWrapper>
      </PageContainer>
    </PageContent>
  );
};
export default AdminStudentPage;
